var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.entertainment",
  title: "EGT Entertainment page",
  description: "Stories / EGT Studios — an album showcase. Released works under EGT Studios (ASCAP), live on Apple Music, Spotify and YouTube Music.",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");

/* The released works, under EGT Studios (ASCAP). Music is present-tense and
 * real (Apple Music + Spotify + YouTube Music). Shared from the single ALBUMS
 * source so home + studios never drift. NEVER print "The Universe, EGT" as artist.
 * The Apple-link swap (Blueprints / This is My Christmas) is corrected in the
 * shared source — do NOT regress it here. */
var ALBUMS = S.ALBUMS;

/* RELEASED vs PRERELEASE is DATA-DRIVEN off the single ALBUMS source, not a
 * hand-maintained list: S.albumReleased(a) compares each album's `releaseDate`
 * to now at render time (the auto-flip). Every album — released or prerelease —
 * renders in the SAME frosted-glass card format in ONE grid (albumItem). The
 * prereleases (Acts 2/3, future dates carrying `presaveUrl`) sit FIRST, at full
 * opacity, with "Releases on <date>" + a pre-save CTA in place of streaming
 * links; they convert themselves to released cards on 2026-08-07 / 2026-08-21
 * with no code change. `released` is still isolated for the JSON-LD (a
 * prerelease has no streaming `sameAs` yet and must not assert a live album).
 *
 * DATE POSTURE: the /entertainment prerelease card now shows the EXACT
 * "Releases on <Month D, YYYY>" (founder directive 2026-07-31), matching the
 * detail page — this SUPERSEDES the earlier month-only "window" treatment and
 * the removed semi-transparent upcoming strip. Released works carry no date. */
var released = ALBUMS.filter(function (a) { return S.albumReleased(a); });
var upcoming = ALBUMS.filter(function (a) { return !S.albumReleased(a); });
/* Ordered showcase: prereleases first (Act 2 then Act 3, in ALBUMS order),
 * then the released works — one consistent grid. */
var ordered = upcoming.concat(released);

/* One album — released OR prerelease — rendered identically for BOTH views as
 * its OWN frosted-glass card (.egt-glass, same primitive as the home studios
 * panel). CSS reflows it per the panel's data-egt-view: a big art card (card
 * view) or an art-led row (list view). The cover art is staged large in both —
 * prereleases use their REAL covers (/imgs/albums/exploit-act-2.jpg, -act-3.jpg)
 * at FULL opacity, never dimmed and never scrimmed (§4).
 *
 * Trailing action: RELEASED → icon-only Apple / Spotify / YouTube Music links
 * (S.streamLinks); PRERELEASE → "Releases on <date>" in the meta + a DistroKid
 * pre-save CTA (external hop → target=_blank + rel=noopener noreferrer, aria
 * naming the record). No streaming links on a prerelease, so the card never
 * implies the record is streamable yet. Fully usable with JS off. */
function albumItem(a, i) {
  var href = "/entertainment/" + a.id;
  /* Posture keys off STREAMABLE (released AND has ≥1 link), never raw
   * albumReleased — a released-but-not-yet-linked record (Act 3 once its date
   * passes but before links land) must show the presave posture, never an empty
   * pills box. "Releases on <date>" keys off albumReleased so a past-dated record
   * never prints a stale future date. */
  var streamable = S.albumStreamable(a);
  var released = S.albumReleased(a);

  var meta = [
    { n: "h3", c: "egt-alb__title",
      i: { n: "a", href: href, c: "egt-alb__title-lk", i: a.nm } },
    { n: "p", c: "egt-alb__sub", i: a.meta }
  ];
  if (!released) {
    meta.push({ n: "p", c: "egt-alb__release",
      i: [ "Releases on ", { n: "span", c: "egt__gold", i: S.albumReleaseDateLong(a) } ] });
  }

  var action = streamable
    ? { n: "div", c: "egt-alb__links", i: S.streamLinks(a.apple, a.spotify, a.youtube) }
    : (a.presaveUrl
        ? { n: "a", href: a.presaveUrl, target: "_blank", rel: "noopener noreferrer",
            c: "egt-cta egt-alb__cta", "aria-label": "Pre-save " + a.nm + " on DistroKid",
            i: [ "Pre-save", { n: "span", c: "egt-cta__tick", i: " ›" } ] }
        : null);

  return {
    n: "article",
    c: "egt-glass egt-alb egt-lift egt-reveal" + (streamable ? "" : " egt-alb--pre"),
    id: "egt-album-" + a.id,
    style: "--egt-i:" + i, "data-egt-art": a.art,
    role: "group", "aria-roledescription": streamable ? "album" : "upcoming album",
    "aria-label": a.nm + " — " + a.meta,
    i: [
      /* cover + title link to the album's own detail page (full tracklist +
       * streaming/presave); the icon-only streaming pills remain direct links. */
      { n: "a", href: href, c: "egt-alb__cover egt-alb__cover-lk",
        "aria-label": "Open the " + a.nm + " album page",
        i: { n: "img", c: "egt-alb__img", src: a.art, alt: a.nm + " — album art",
          loading: "lazy", decoding: "async", width: "600", height: "600" } },
      { n: "div", c: "egt-alb__meta", i: meta }
    ].concat(action ? [ action ] : [])
  };
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList + one MusicAlbum per released
 * work in the showcase. Only REAL, on-page facts are used: album names + the
 * "Music by EGT Studios · ASCAP" credit (byArtist), the cover art image, and
 * the three streaming links shown as icons (sameAs), and datePublished = the
 * real releaseDate (decision D001). No fabricated tracklist. Works whose on-page meta reads
 * "Single" are typed with albumReleaseType SingleRelease; the rest default to
 * AlbumRelease. Site-wide Organization + WebSite come from S.page; URLs from
 * S.siteOrigin(). */
function studiosSchema() {
  var O = S.siteOrigin();
  /* STREAMABLE works only — a prerelease (or a released record with no links
   * yet) has no streaming `sameAs` and must not assert a live MusicAlbum with an
   * empty/undefined sameAs. */
  var albums = ALBUMS.filter(function (a) { return S.albumStreamable(a); }).map(function (a) {
    var node = {
      "@type": "MusicAlbum",
      /* The SAME @id the album's own page emits (egt.album.js), so the hub
       * and the detail page describe one entity, not two. SEO wave 2B-6. */
      "@id": O + "/entertainment/" + a.id + "#album",
      url: O + "/entertainment/" + a.id,
      name: a.nm,
      byArtist: { "@type": "MusicGroup", name: "EGT Studios" },
      image: O + a.art,
      albumReleaseType: a.meta === "Single"
        ? "https://schema.org/SingleRelease"
        : "https://schema.org/AlbumRelease",
      sameAs: [ a.apple, a.spotify, a.youtubeAlbum || a.youtube ].filter(Boolean)
    };
    if (a.releaseDate) { node.datePublished = a.releaseDate; }
    return node;
  });
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/entertainment#webpage", url: O + "/entertainment",
      /* matches the page <title> and the visible H1 (SEO wave 2C-2) */
      name: "EGT Entertainment",
      isPartOf: { "@id": O + "/#website" },
      breadcrumb: { "@id": O + "/entertainment#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/entertainment#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Entertainment" }
      ] }
  ].concat(albums));
}

exports.render = async function render() {
  return S.page("/reserve", [

    studiosSchema(),

    /* ---- THE BOOK — Exploit, Book One of The Universe. The FLAGSHIP: a
     * commanding, near-full-viewport HERO (WP-N7.2) — the book's grand entrance,
     * not a small tile above the albums. The title-card art is staged BIG as the
     * centerpiece, over the UNDIMMED hero background (contrast comes from the
     * frosted card only, never a scrim, §4). Eyebrow + a Playfair thriller line +
     * a warm lead + a confident CTA out to /exploit sit on the frosted card.
     * Honest posture: a real novel; no dates, no film/series. */
    S.canvas({
      id: "exploit",
      c: "egt-st-exploit",
      align: "center",
      media: { img: "/imgs/exploit/scene-reyna-comes.jpg", priority: true },
      content: [
        /* The page's ONE <h1> — visible, set as a kicker line above the book's
         * own eyebrow, in the page's existing name (SEO wave 2C-2). */
        { n: "h1", c: "egt__eyebrow egt-st-exploit__h1", i: "EGT Entertainment" },
        { n: "p", c: "egt__eyebrow", i: "Book One of The Universe" },
        { n: "img", c: "egt-st-book__title-img", src: "/imgs/exploit/exploit-title-card.avif",
          alt: "Exploit", loading: "eager", decoding: "async" },
        { n: "p", c: "egt-st-exploit__tag", i: "A cinematic spiritual thriller." },
        { n: "p", c: "egt-section__lead egt-st-exploit__lead",
          i: "The novel at the heart of the Universe — meet the souls, walk the world." },
        { n: "a", href: "/exploit", c: "egt-cta egt-st-exploit__cta",
          i: [ "Read Exploit", { n: "span", c: "egt-cta__tick", i: " ›" } ] }
      ]
    }),

    /* ---- THE FILM — The Journey of the Coin, the first film of the Universe
     * (founder directive 2026-10-05: the short film belongs on this page). The
     * undimmed frame of the woman with the glowing coin; the frosted card sits
     * on the right so she and the coin stay in view (§4 — glass, never a scrim). */
    S.canvas({
      id: "film",
      c: "egt-st-film",
      align: "end",
      media: { img: "/imgs/coin/j06.jpg" },
      content: [
        { n: "p", c: "egt__eyebrow", i: [ S.egtMark(), " — A Short Film" ] },
        { n: "h2", c: "egt-section__title egt-st-film__title", i: "The Journey of the Coin" },
        { n: "p", c: "egt-section__lead",
          i: S.brand("A small token, seemingly insignificant, with a profound impact on time and on life. The 3-minute-45 prelude to the EGT Universe, made with the EGT* AI Film Studio.") },
        { n: "div", c: "egt-st-film__actions",
          i: [
            { n: "a", href: "/entertainment/the-journey-of-the-coin#watch", c: "egt-cta egt-cta--primary",
              i: [ "Watch the film", { n: "span", c: "egt-cta__tick", i: " ›" } ] },
            { n: "a", href: "/entertainment/the-journey-of-the-coin", c: "egt-cta egt-cta--ghost",
              i: [ "The making of", { n: "span", c: "egt-cta__tick", i: " ›" } ] }
          ] }
      ]
    }),

    /* ---- THE RECORDS — album showcase as a list<->card toggle grid -----
     * (first hero removed per founder.) The whole page is the covers, staged on
     * frosted glass over the cinematic still (NEVER dimmed — contrast is glass).
     * data-egt-view-root + the right-most S.viewToggle drive list<->card via the
     * shared client (initViewToggle in /src/egt.home.js). Rests in CARD view. */
    { n: "section", c: "egt-section egt-canvas egt-st-albums", id: "wall",
      "data-egt-view-root": "true", "data-egt-view": "card",
      i: [
        /* WP-N7.3: the "Wherever you listen." block owns its OWN media entry
         * (/imgs/st-listen.jpg — a darker, epic music still) so it no longer
         * shares egt-studios-canvas.jpg and can be swapped independently in the
         * media manager. UNDIMMED (contrast is the white heading + the covers'
         * own shadows), .jpg referenced literally so it inventories as its own. */
        { n: "div", c: "egt-canvas__media", "aria-hidden": "true", "data-egt-parallax": "0.06",
          i: { n: "img", c: "egt-canvas__img", src: "/imgs/st-listen.jpg",
            alt: "", loading: "lazy", decoding: "async" } },
        { n: "div", c: "egt__shell egt-canvas__inner egt-st-albums__inner",
          i: [
            { n: "div", c: "egt-st-albums__bar egt-reveal",
              i: [
                { n: "div", c: "egt-st-albums__head",
                  i: [
                    { n: "p", c: "egt__eyebrow", i: "The records" },
                    { n: "h2", c: "egt-section__title egt-st-albums__heading",
                      i: [ "Wherever ", { n: "span", c: "egt__gold", i: "you listen." } ] }
                  ] },
                /* the view toggle is always the right-most control */
                S.viewToggle("card")
              ] },
            /* ONE grid, prereleases first (see `ordered`): every album is a
             * frosted-glass card; released cards carry streaming pills,
             * prerelease cards carry "Releases on <date>" + a pre-save CTA. Any
             * ALBUMS entry with a future releaseDate renders as a prerelease
             * card and auto-flips to released on its date. */
            { n: "div", c: "egt-st-albums__grid", "data-egt-view-body": "true",
              role: "group", "aria-label": "EGT Studios albums",
              i: ordered.map(albumItem) },
            /* + an inbound link to /classicman (SEO wave 2C-13) — EGT presents
             * the show (the visible sponsor line on /classicman). */
            { n: "p", c: "egt-st-albums__credit",
              i: [ "Music by EGT Studios™ · ASCAP. EGT also presents ",
                { n: "a", href: "/classicman", c: "egt-st-albums__credit-link", i: "The Classic Man Podcast" }, "." ] }
          ] }
      ] },

    /* ---- THE BRIDGE — music → the book (STR-3 / Week-0 gate).
     * This page is where album traffic lands, and until now the only capture on
     * it was the footer strip. You cannot deep-link out of a Spotify playback,
     * so every clickable path off the music has to live on the page the music
     * points at — and it must ask for the email, not the $75. Placed directly
     * under the covers, while they are still looking at the record.
     *
     * source:"album" → the ONE `general` newsletter (see egt.capture.js SOURCES)
     * AND a key of PROMISED_SOURCES (egt.free-sample.js), so this door really
     * does deliver the free opening on confirm.
     *
     * COPY IS WRITTEN FOR A MIXED CATALOGUE. This hub lists ALL the records, and
     * only the three Exploit acts are scored to the novel — the other eight are
     * not. It therefore must NOT say "these songs are scored to a novel" (it did
     * until 2026-08-21); it names the studio as the common thread and says which
     * records are the score. Same honesty rule the per-album detail pages follow
     * (egt.album.js): never tell a listener a record is scored to a book it
     * isn't. */
    S.capture({
      source: "album",
      variant: "panel",
      eyebrow: "The studio also writes books",
      title: [ "Read the opening of our novel ", { n: "span", c: "egt__gold", i: "free." } ],
      lead: "The studio behind these records also publishes a novel — Exploit, Book One of The Universe. The three Exploit acts above are its score. Tell us where to send it and the opening chapters are yours — free.",
      label: "Where should we send it?",
      cta: "Send me the free chapters",
      /* Marketing-list consent, REQUIRED + never pre-checked (founder ruling
       * 2026-07-22). The label names the free opening, because that is what this
       * door promises — same wording as the other book doors. */
      consent: true,
      consentLabel: [ "Yes — email me the free opening of ", { n: "em", i: "Exploit" }, ", then new chapters, album drops and news from EGT. I can unsubscribe from any email." ],
      /* THE CONFIRM/SPAM DIALOG — required on every door that promises the book
       * (delivery is gated on the double-opt-in click). This door was missing it
       * for the same reason the album detail door was; both fixed 2026-08-21. */
      dialog: true,
      dialogLead: "You're confirmed, and the free opening of Exploit is yours. Tap Read your free chapters to start."
    }),

    /* ---- THE CLOSE — one music-only quote (attribution removed per founder) */
    S.quote(
      [ "Press play.", { n: "br" },
        { n: "span", c: "egt__gold", i: "The rest of the house can't wait." } ]
    )
  ], { footerCaptureLabel: S.FOOTER_LABELS.entertainment });
};
